<script setup>

    import { ref, computed, unref, useTemplateRef } from 'vue'
    import { useStorage } from '@vueuse/core'
    import { storeToRefs } from 'pinia'

    import ProgressBarTiny from '@/Components/ProgressBarTiny.vue'

    import { MinusIcon, QueueListIcon} from '@heroicons/vue/20/solid'
    import { IconArrowAutofitWidth20Filled, IconArrowFitIn20Filled } from '@iconify-prerendered/vue-fluent'

    import { useViewStore } from '@/Stores'
    import { util } from '@/Helpers'

    const viewStore = useViewStore()

    const props = defineProps({
        id: {
            type: String,
            default: null
        },
        title: {
            type: String,
            default: 'Untitled'
        },
        centerHeaderSlot: {
            type: Boolean,
            default: true
        },
        collapsible: {
            type: Boolean
        },
        supports: {
            type: Array,
            default: () => [ ] // collapsible, widescreen
        },
        start: {
            type: Number,
        },
        end: {
            type: Number
        },
        progress: {
            type: Object
        },
        forceSingleLineMobileLayout: {
            type: Boolean,
            default: false
        },
        isEmbedded: {
            type: Boolean,
            default: false
        }
    })

    const features = computed(() => {
        return {
            collapsible: props.collapsible || props.supports.includes('collapsible'),
            widescreen: props.supports.includes('widescreen'),
        };
    })

    const collapsed = props.isEmbedded ? ref(false) : useStorage(props.id + '-collapsed', false)

    const widescreen = props.isEmbedded ? false : useStorage(props.id + '-widescreen', false)

    const { scrollDist } = storeToRefs(viewStore)

    const headerEl = useTemplateRef('headerEl')

    const viewportOffset = computed(() => {
        // forces recomputation using scrollDist
        return util.getOffset(headerEl.value).top + scrollDist.value - scrollDist.value;
    })

    const positionTop = computed(() => {
        if (!headerEl.value) { return false; }
        const navBarElHeight = document.getElementById('latte-navbar') ? document.getElementById('latte-navbar').offsetHeight : 0
        return viewportOffset.value-navBarElHeight;
    })

    const isStickied = computed(() => {
        if (unref(collapsed) || props.isEmbedded == true) { return false; }
        return ((positionTop.value-scrollDist.value) <= 0);
    })

    const formatProgressTooltip = (items = []) => {
        if (!Array.isArray(items) || items.length === 0) {
            return '';
        }
        return 'Progress:\n' + items
            .filter((item) => item && typeof item.label === 'string' && item.value !== undefined)
            .map((item) => `— ${item.label}: ${String(item.value)}%`)
            .join('\n');
    };


</script>

<template>

    <div
        :id="props.id"
        :class="[
            'mx-auto shadow-better',
            {
                'max-w-5xl': !widescreen,
                'max-w-full': widescreen,
                'rounded-lg': collapsed,
                'my-4': props.isEmbedded,
                'my-12': !props.isEmbedded,
                'lg:rounded-t-lg': !isStickied && !collapsed,
                'rounded-t-none': isStickied && !collapsed,
            }
        ]"
        ref="headerEl"
    >

        <div
            :class="[
                'flex flex-col md:flex-row md:items-start gap-2 md:gap-0 md:space-x-2 bg-lifeworx-blue-700 px-4 py-2.5 border-b border-lifeworx-blue-600 transition duration-200 top-16',
                {
                    'sticky': !props.isEmbedded,
                    'lg:rounded-b-lg bg-lifeworx-blue-700 bg-opacity-70': collapsed,
                    'md:h-12': forceSingleLineMobileLayout,
                    'md:h-20': !forceSingleLineMobileLayout,
                    'lg:rounded-t-lg z-module-container': !isStickied && !collapsed,
                    'rounded-t-none z-modal': isStickied && !collapsed,
                    'rounded-lg z-module-container': collapsed
                }
            ]"
        >

            <div
                :class="[
                    'flex min-w-0 items-center space-x-2 md:space-x-8',
                    {
                        'w-full md:w-1/3': props.centerHeaderSlot,
                        'w-full md:w-auto md:shrink-0': !props.centerHeaderSlot
                    }
                ]"
            >
                <div
                    :class="{
                        'flex w-auto': forceSingleLineMobileLayout,
                        'flex-none w-24 md:flex md:w-auto': !forceSingleLineMobileLayout,
                    }"
                >
                    <h2 class="mt-0.5 mr-5 text-white text-sm md:text-base font-semibold drop-shadow tracking-wide select-none cursor-default md:text-nowrap">
                        {{ title }}
                    </h2>
                </div>

            </div>

            <div
                :class="[
                    'flex w-full min-w-0 self-center',
                    {
                        'flex-grow': !props.centerHeaderSlot,
                        'md:w-2/3': props.centerHeaderSlot
                    }
                ]"
            >
                <div class="flex w-full min-w-0 flex-col gap-2 md:flex-row md:gap-0 md:space-x-8">
                    <div
                        :class="[
                            'flex w-full min-w-0 items-center',
                            {
                                'flex-grow': !props.centerHeaderSlot,
                                'md:w-1/2': props.centerHeaderSlot
                            }
                        ]"
                    >
                        <slot
                            name="header"
                            v-if="!collapsed"
                        />
                    </div>

                    <div
                        :class="[
                            'flex w-full min-w-0 justify-end items-center gap-2 md:gap-0',
                            {
                                'flex-grow': !props.centerHeaderSlot,
                                'md:w-1/2': props.centerHeaderSlot
                            }
                        ]"
                    >

                        <div
                            v-if="props.progress !== undefined"
                            :class="[
                                'relative -left-5 flex transition-opacity duration-100',
                                {
                                    'opacity-0': !collapsed,
                                    'opacity-100': collapsed || isStickied
                                }
                            ]"
                        >

                            <div
                                class="w-full flex items-center justify-end mr-5"
                                v-tooltip.bottom="{
                                    value: formatProgressTooltip(progress),
                                    disabled: !isStickied,
                                    pt: {
                                        root: '!z-modal text-sm font-semibold',
                                        arrow: '!z-modal',
                                    }
                                }"
                            >
                                <div class="min-w-40">
                                    <MeterGroup
                                        :value="progress"
                                        :pt="{
                                            root: 'rounded-md',
                                            meters: 'border-[3px] border-lifeworx-blue-700 ring-1 ring-offset-1 ring-white h-4 rounded-lg overflow-hidden bg-white',
                                            meter: 'rounded-none',
                                            labelList: 'hidden',
                                        }"
                                    />
                                </div>
                            </div>

                        </div>

                        <slot
                            name="header-actions"
                            v-if="!collapsed"
                        />

                        <button
                            v-if="features.widescreen"
                            @click="widescreen=!widescreen"
                            class="hidden lg:flex h-10 w-10 mt-0.5 size-7 p-1 grow-0 justify-self-end items-center justify-center rounded-md text-white hover:bg-lifeworx-blue-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-lifeworx-blue-700"
                        >
                            <span class="sr-only">Collapse</span>
                            <IconArrowAutofitWidth20Filled v-if="!widescreen" class="block h-5 w-5" aria-hidden="true" />
                            <IconArrowFitIn20Filled v-else class="block h-5 w-5" aria-hidden="true" />
                        </button>

                        <button
                            v-if="features.collapsible"
                            @click="collapsed=!collapsed"
                            type="button"
                            data-test="container-collapse-toggle"
                            :aria-expanded="!collapsed"
                            :aria-controls="props.id ? `${props.id}-body` : undefined"
                            class="flex shrink-0 h-10 w-10 mt-0.5 md:h-7 md:w-7 p-1 grow-0 justify-self-end items-center justify-center rounded-md text-white hover:bg-lifeworx-blue-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-lifeworx-blue-700"
                        >
                            <span class="sr-only">{{ collapsed ? 'Expand' : 'Collapse' }}</span>
                            <MinusIcon v-if="!collapsed" class="block h-5 w-5" aria-hidden="true" />
                            <QueueListIcon v-else class="block h-5 w-5" aria-hidden="true" />
                        </button>

                    </div>

                </div>
            </div>

        </div>

        <div
            :id="props.id ? `${props.id}-body` : undefined"
            :class="[
                'bg-white',
                {
                    'hidden': collapsed
                }
            ]"
        >
            <slot />
        </div>

    </div>
    <div
        :class="[
            'transition-all duration-75 overflow-clip',
            {
                'h-0': collapsed,
                'h-fit': !collapsed
            }
        ]"
    >
        <slot name="below" />
    </div>

</template>

